import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useSettings } from '../../providers/settings-provider';
import { useLanguage } from '../../contexts/LanguageContext';

// Types for sidebar menu items
interface SidebarMenuItem {
  labelKey: string;
  icon: string;
  path: string;
}

// Sidebar menu configuration with custom SVG icons
const SIDEBAR_MENU_ITEMS: SidebarMenuItem[] = [
  {
    labelKey: 'common.dashboard',
    icon: '/img/dashboard.svg',
    path: '/dashboard',
  },
  {
    labelKey: 'common.fulfillment',
    icon: '/img/fulfillment.svg',
    path: '/fulfillment',
  },
  {
    labelKey: 'common.operation',
    icon: '/img/operation.svg',
    path: '/operation',
  },
  {
    labelKey: 'common.report',
    icon: '/img/report.svg',
    path: '/report',
  },
  {
    labelKey: 'common.settings',
    icon: '/img/setting.svg',
    path: '/settings',
  },
];

// Sidebar Header Component
function SidebarHeader() {
  return (
    <div className="sidebar-header hidden lg:flex items-center justify-center px-3 lg:px-6 shrink-0 mt-12 mb-17">
      <Link to="/">
      <img src="/img/logo.png" alt="Logo" className="" />
      </Link>
    </div>
  );
}

// Sidebar Menu Component
function SidebarMenu() {
  const { pathname } = useLocation();
  const { t } = useLanguage();

  return (
    <nav className="flex flex-col gap-8 mt-8">
      {SIDEBAR_MENU_ITEMS.map((item) => {
        const isActive = pathname === item.path;
        
        return (
          <Link
            key={item.path}
            to={item.path}
            className="flex flex-col items-center gap-2 py-2 transition-colors"
          >
            <div className={cn(
              'w-11 h-11 rounded-lg flex items-center justify-center transition-colors',
              isActive 
                ? 'bg-primary-500' 
                : 'bg-gray-100 hover:bg-gray-200'
            )}>
              <img
                src={item.icon}
                alt={`${t(item.labelKey)} icon`}
                className="w-6 h-6 object-contain"
              />
            </div>
            <span className={cn(
              'text-xs font-semibold text-center',
              isActive ? 'text-primary-500' : 'text-white'
            )}>
              {t(item.labelKey)}
            </span>
          </Link>
        );
      })}
    </nav>
  );
}

// User Avatar Component
function UserAvatar() {
  return (
    <div className="flex flex-col items-center mb-6">
      <img
        src="/img/man.png"
        alt="User Avatar"
        className="w-[58px] h-[58px]"
      />
    </div>
  );
}

// Mobile Drawer Component
function MobileDrawer({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 bg-black/40 md:hidden" onClick={onClose}>
      <div
        className="bg-[#1B254B] w-32 h-full flex flex-col justify-between p-0 shadow-lg"
        onClick={(e) => e.stopPropagation()}
      >
        <div>
          <SidebarHeader />
          <SidebarMenu />
        </div>
        <UserAvatar />
        
        {/* Close button for mobile */}
        <button
          className="absolute top-4 right-4 text-white hover:text-gray-300"
          onClick={onClose}
          aria-label="Close sidebar"
        >
          <X size={24} />
        </button>
      </div>
    </div>
  );
}

// Main Sidebar Component
export function Sidebar() {
  const { settings } = useSettings();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      {/* Hamburger menu for mobile */}
      <button
        className="fixed top-4 left-4 z-50 md:hidden bg-[#1B254B] p-2 rounded-full shadow text-white"
        onClick={() => setDrawerOpen(true)}
        aria-label="Open sidebar menu"
      >
        <Menu size={24} />
      </button>

      {/* Mobile drawer */}
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      {/* Fixed sidebar for desktop */}
      <div
        className={cn(
          'fixed top-0 left-0 h-full w-24 bg-[#1B254B] z-30 hidden lg:flex flex-col justify-between',
          (settings?.sidebarTheme === 'dark' || pathname.includes('dashboard')) && 'dark'
        )}
      >
        <div>
          <SidebarHeader />
          <SidebarMenu />
        </div>
        <UserAvatar />
      </div>
    </>
  );
}
